import { useEffect } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook that automatically tracks user activity and refreshes session
 * Listens to mouse, keyboard, and touch events to detect user interaction
 */
export function useActivityTracker() {
  const { refreshActivity, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity refresh to avoid excessive calls
    let throttleTimeout: NodeJS.Timeout | null = null;
    const THROTTLE_DELAY = 30000; // 30 seconds

    const handleActivity = () => {
      if (throttleTimeout) {
        return; // Already scheduled
      }

      throttleTimeout = setTimeout(() => {
        refreshActivity();
        throttleTimeout = null;
      }, THROTTLE_DELAY);
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial activity refresh
    refreshActivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [isAuthenticated, refreshActivity]);
}